<template>
    <div class="adaptation-block">
        <main class="adaptation-block__column my-home-page home-editor" :inert="showDiscard || showLockConfirm ? '' : null">
            <MyHomePageHeader :title="$t(isPhotos ? 'myHome.videoDoorbells' : 'myHome.vehicles')" :disabled="saving" @back="$router.push('/my-home')" />
            <p v-if="loading" class="home-editor__status" role="status">{{ $t('myHome.loading') }}</p>
            <div v-else-if="loadError" class="home-editor__status" role="alert">
                <p>{{ $t(loadError) }}</p>
                <button type="button" @click="retryKey++">{{ $t('myHome.retry') }}</button>
            </div>
            <template v-else-if="home?.has_access">
                <p class="home-editor__description" :class="{ 'home-editor__description--vehicles': !isPhotos }">{{ $t(isPhotos ? 'myHome.photoDescription' : 'myHome.vehicleDescription', { limit }) }}</p>
                <form class="home-editor__form" @submit.prevent="save">
                    <fieldset :disabled="saving" class="home-editor__fields">
                        <div class="home-editor__grid" :class="{ 'home-editor__grid--vehicles': !isPhotos }">
                            <div v-for="(item, index) in draft" :key="item.key" class="home-editor__item">
                                <template v-if="isPhotos">
                                    <label class="home-editor__portrait home-editor__corners" :aria-label="$t('myHome.choosePhoto', { number: index + 1 })">
                                        <img v-if="item.preview" :src="item.preview" :alt="item.name || $t('myHome.profile', { number: index + 1 })">
                                        <svg v-else-if="item.file || item.id" viewBox="0 0 90 90" aria-hidden="true"><circle cx="45" cy="32" r="13"/><path d="M21 76V66a24 24 0 0 1 48 0v10"/></svg>
                                        <svg v-else viewBox="0 0 84 84" aria-hidden="true" class="home-editor__plus"><path d="M42 25V59M25 42H59"/></svg>
                                        <input type="file" :accept="PHOTO_ACCEPT" :aria-label="$t('myHome.choosePhoto', { number: index + 1 })" @change="choosePhoto($event, item)">
                                    </label>
                                    <p v-if="item.file && !item.preview" class="home-editor__description home-editor__selected-photo" role="status">{{ $t('myHome.photoSelected', { name: item.file.name }) }}</p>
                                </template>
                                <div v-else class="home-editor__plate" :class="{ 'home-editor__plate--empty': !item.plate_number }">
                                    <HomeLicensePlate number="" />
                                    <input v-model="item.plate_number" :disabled="isLocked(item)" :aria-describedby="isLocked(item) ? `plate-lock-${item.key}` : null" maxlength="20" :aria-label="$t('myHome.plateNumber', { number: index + 1 })" :placeholder="'XX XX XX XX'" autocomplete="off" spellcheck="false">
                                </div>
                                <label class="home-editor__name home-editor__corners">
                                    <span class="home-editor__sr">{{ $t(isPhotos ? 'myHome.profile' : 'myHome.vehicle', { number: index + 1 }) }}</span>
                                    <input v-model="item.name" maxlength="255" :placeholder="$t(isPhotos ? 'myHome.enterName' : 'myHome.enterVehicleName')">
                                </label>
                                <p v-if="isLocked(item)" :id="`plate-lock-${item.key}`" class="home-editor__lock">{{ $t(lockLabel(item).key, { count: lockLabel(item).count }) }}</p>
                                <button v-else-if="item.id || item.file || item.name || item.plate_number" type="button" class="home-editor__delete home-editor__corners" :aria-label="$t('myHome.removeItem', { number: index + 1 })" @click="remove(item)">
                                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h18M9 6V3h6v3M6 6l1 15h10l1-15M10 9v9M14 9v9"/></svg>
                                </button>
                            </div>
                        </div>
                    </fieldset>
                    <p v-if="!draft.length" class="home-editor__description">{{ $t('myHome.limitExceeded') }}</p>
                    <p v-if="saveError" class="home-editor__status home-editor__error" role="alert">{{ $t(saveError) }}</p>
                    <button type="submit" class="home-editor__save" :disabled="saving || choosingPhoto || !dirty || loading">
                        <span class="home-editor__save-label">{{ $t(saving ? 'myHome.saving' : 'myHome.save') }}</span>
                        <svg viewBox="0 0 357 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M7 2H350L356 8V40L350 46H7L1 40V8L7 2Z" fill="currentColor" fill-opacity="0.59"/>
                            <path d="M2 9L9 2H348L355 9" stroke="currentColor" stroke-width="3"/>
                            <path d="M355 39L348 46H8L2 39" stroke="currentColor" stroke-width="3"/>
                        </svg>
                    </button>
                </form>
            </template>
        </main>
        <div v-if="showLockConfirm" class="home-editor__overlay" @keydown.esc.prevent="resolveLockConfirm(false)" @keydown.tab.prevent="cycleLockFocus">
            <section role="dialog" aria-modal="true" aria-labelledby="home-lock-title" aria-describedby="home-lock-message" class="home-editor__dialog">
                <h2 id="home-lock-title">{{ $t('myHome.plateLockTitle') }}</h2>
                <p id="home-lock-message">{{ $t('myHome.plateLockConfirm') }}</p>
                <button ref="lockCancelButton" type="button" @click="resolveLockConfirm(false)">{{ $t('myHome.keepEditing') }}</button>
                <button ref="lockSaveButton" type="button" @click="resolveLockConfirm(true)">{{ $t('myHome.save') }}</button>
            </section>
        </div>
        <div v-if="showDiscard" class="home-editor__overlay" @keydown.esc.prevent="resolveDiscard(false)" @keydown.tab.prevent="cycleDialogFocus">
            <section role="dialog" aria-modal="true" aria-labelledby="home-discard-title" aria-describedby="home-discard-message" class="home-editor__dialog">
                <h2 id="home-discard-title">{{ $t('myHome.unsavedTitle') }}</h2>
                <p id="home-discard-message">{{ $t('myHome.discardChanges') }}</p>
                <button ref="cancelButton" type="button" @click="resolveDiscard(false)">{{ $t('myHome.keepEditing') }}</button>
                <button ref="discardButton" type="button" class="home-editor__discard" @click="resolveDiscard(true)">{{ $t('myHome.leaveWithoutSaving') }}</button>
            </section>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRouter } from 'vue-router'
import { useMainStore } from '@/store/mainStore'
import ApiManager from '@/services/ApiManager'
import { getHomeAuthToken } from '@/services/myHome'
import { PHOTO_ACCEPT, photoMimeType, draftSignature, validateDraft, photoSaveForm, vehicleSaveItems, normalizePlate } from '@/services/myHomeDraft'
import HomeLicensePlate from '@/components/HomeLicensePlate.vue'
import MyHomePageHeader from '@/components/MyHomePageHeader.vue'

export default {
    name: 'MyHomeAccessEditor',
    components: { HomeLicensePlate, MyHomePageHeader },
    props: { kind: { type: String, required: true } },
    setup(props) {
        const store = useMainStore()
        const router = useRouter()
        const home = ref(null), draft = ref([]), loading = ref(true), saving = ref(false)
        const loadError = ref(null), saveError = ref(null), retryKey = ref(0)
        const baseline = ref('[]'), choosingPhoto = ref(0)
        const showLockConfirm = ref(false), lockCancelButton = ref(null), lockSaveButton = ref(null)
        let lockResolver = null, lockPreviousFocus = null
        const clockNow = ref(Date.now())
        let clockBase = Date.now(), clockStarted = performance.now(), clockTimer = null
        function syncClock(data) {
            const time = Date.parse(data.server_time)
            clockBase = Number.isFinite(time) ? time : Date.now()
            clockStarted = performance.now()
            tickClock()
        }
        function tickClock() { clockNow.value = clockBase + performance.now() - clockStarted }
        function remaining(item) {
            if (isPhotos.value || !item.id) return 0
            const plate = home.value?.vehicles?.find(plate => plate.id === item.id)
            return Math.max(0, (Date.parse(plate?.mobile_locked_until) || 0) - clockNow.value)
        }
        function isLocked(item) { return remaining(item) > 0 }
        function lockLabel(item) {
            const ms = remaining(item)
            return ms >= 3600000 ? { key: 'myHome.plateLockedHours', count: Math.ceil(ms / 3600000) } :
                { key: 'myHome.plateLockedMinutes', count: Math.ceil(ms / 60000) }
        }
        async function refreshLocks(force = false) {
            if (isPhotos.value || !token || loading.value || (saving.value && !force)) return
            const current = generation
            try {
                const result = await ApiManager.getMyHomeDetails(token)
                if (current !== generation) return
                syncClock(result.data)
                if (home.value && result.data.has_access) {
                    const latest = new Map(result.data.vehicles.map(plate => [plate.id, plate]))
                    home.value.vehicles = home.value.vehicles.map(plate => ({ ...plate,
                        mobile_locked_until: latest.get(plate.id)?.mobile_locked_until ?? null,
                    }))
                }
            } catch { /* Preserve the draft; the server still enforces locks on Save. */ }
        }
        function foreground() { tickClock(); if (document.visibilityState === 'visible') refreshLocks() }
        const showDiscard = ref(false), cancelButton = ref(null), discardButton = ref(null)
        let discardPromise = null, discardResolver = null, previousFocus = null
        const isPhotos = computed(() => props.kind === 'photos')
        const limit = computed(() => home.value?.limits?.[props.kind] || 0)
        const dirty = computed(() => choosingPhoto.value > 0 || draftSignature(draft.value) !== baseline.value)
        let sequence = 0, token = null, generation = 0
        let urls = []
        function emptyItem() { return { key: ++sequence, name: '', plate_number: '', file: null, preview: null } }
        function populate(data) {
            draft.value = data[props.kind].map(item => ({ ...emptyItem(), ...item, name: item.name || '' }))
            // Keep legacy over-limit records editable so users can remove them.
            while (draft.value.length < limit.value) draft.value.push(emptyItem())
            baseline.value = draftSignature(draft.value)
        }
        watch(() => [store.login, props.kind, retryKey.value], async ([login], previous, onCleanup) => {
            const current = ++generation
            const controller = new AbortController()
            onCleanup(() => { controller.abort(); urls.forEach(url => URL.revokeObjectURL(url)); urls = [] })
            resolveLockConfirm(false)
            home.value = null; draft.value = []; baseline.value = '[]'; token = null; choosingPhoto.value = 0
            loading.value = true; loadError.value = null; saveError.value = null
            try {
                if (!login) throw new Error('Missing account')
                const auth = await getHomeAuthToken(login)
                if (current !== generation) return
                token = auth
                const result = await ApiManager.getMyHomeDetails(auth, controller.signal)
                if (current !== generation) return
                home.value = result.data
                if (!home.value.has_access) { loadError.value = 'myHome.noAccess'; return }
                syncClock(home.value)
                populate(home.value)
                loading.value = false
                if (isPhotos.value) await Promise.all(draft.value.filter(item => item.id).map(async item => {
                    try {
                        const photo = await ApiManager.getMyHomePhoto(auth, item.id, controller.signal)
                        if (current !== generation || item.file || !draft.value.includes(item)) return
                        item.preview = URL.createObjectURL(photo.data); urls.push(item.preview)
                    } catch { /* An unavailable existing image can still be renamed, replaced or removed. */ }
                }))
            } catch (error) {
                if (current === generation) loadError.value = error.response?.status === 409 ? 'myHome.ambiguous' :
                    [401, 403, 404].includes(error.response?.status) || !login ? 'myHome.noAccess' : 'myHome.unavailable'
            } finally { if (current === generation) loading.value = false }
        }, { immediate: true, flush: 'sync' })
        function remove(item) {
            if (isLocked(item)) return
            const index = draft.value.indexOf(item)
            draft.value.splice(index, 1)
            if (draft.value.length < limit.value) draft.value.push(emptyItem())
            saveError.value = null
        }
        async function choosePhoto(event, item) {
            const file = event.target.files?.[0]
            event.target.value = ''
            if (!file) return
            if (!photoMimeType(file) || !file.size || file.size > 10 * 1024 * 1024) {
                saveError.value = 'myHome.photoSize'; return
            }
            const selection = (item.selection || 0) + 1
            item.selection = selection
            const preview = URL.createObjectURL(file)
            const current = generation
            choosingPhoto.value++
            try {
                const image = new Image()
                image.src = preview
                // Preview decoding is optional: some browsers cannot display HEIC/HEIF or TIFF.
                // GateOS validates the actual contents and dimensions before storing any photo.
                const decoded = await image.decode().then(() => true, () => false)
                // Leave room for nominal 24 MP images such as 5712 × 4284.
                if (decoded && image.naturalWidth * image.naturalHeight > 25000000) throw new Error('Image too large')
                if (current !== generation || item.selection !== selection || saving.value || !draft.value.includes(item)) { URL.revokeObjectURL(preview); return }
                item.file = file; item.preview = decoded ? preview : null; saveError.value = null
                if (decoded) urls.push(preview)
                else URL.revokeObjectURL(preview)
            } catch {
                URL.revokeObjectURL(preview)
                if (current === generation) saveError.value = 'myHome.photoSize'
            } finally { if (current === generation) choosingPhoto.value-- }
        }
        async function save() {
            if (saving.value || showLockConfirm.value || choosingPhoto.value || !dirty.value || !token) return
            saveError.value = validateDraft(props.kind, draft.value, limit.value)
            if (saveError.value) return
            const current = generation
            if (!isPhotos.value) {
                const changed = vehicleSaveItems(draft.value).some(item => !item.id ||
                    normalizePlate(home.value.vehicles.find(plate => plate.id === item.id)?.plate_number || '') !== item.plate_number)
                if (changed) {
                    lockPreviousFocus = document.activeElement
                    showLockConfirm.value = true
                    const confirmed = await new Promise(resolve => {
                        lockResolver = resolve
                        nextTick(() => lockCancelButton.value?.focus())
                    })
                    if (!confirmed || current !== generation) return
                }
            }
            saving.value = true
            try {
                const result = isPhotos.value ? await ApiManager.saveMyHomePhotos(token, photoSaveForm(draft.value)) :
                    await ApiManager.saveMyHomeVehicles(token, vehicleSaveItems(draft.value))
                if (current !== generation) return
                const previews = draft.value.filter(item => item.id || item.file).map(item => item.preview)
                home.value = result.data
                syncClock(home.value)
                populate(home.value)
                if (isPhotos.value) draft.value.forEach((item, index) => { item.preview = previews[index] || null })
                // Release the navigation guard only after the saved draft becomes the baseline.
                saving.value = false
                await router.push('/my-home')
            } catch (error) {
                if (current === generation) {
                    const locked = !isPhotos.value && !!error.response?.data?.errors?.['vehicles.locked']
                    const uploadLimit = isPhotos.value && !!error.response?.data?.errors?.['photos.upload_limit']
                    saveError.value = uploadLimit ? 'myHome.photoUploadUnavailable' : locked ? 'myHome.plateLockError' :
                        isPhotos.value && error.response?.status === 413 ? 'myHome.photoSize' :
                        error.response?.status === 422 ? (isPhotos.value ? 'myHome.photoSaveInvalid' : 'myHome.saveInvalid') : 'myHome.saveFailed'
                    if (locked) await refreshLocks(true)
                }
            } finally { saving.value = false }
        }
        function resolveLockConfirm(confirm) {
            showLockConfirm.value = false
            lockResolver?.(confirm)
            lockResolver = null
            if (!confirm) nextTick(() => lockPreviousFocus?.focus())
        }
        function cycleLockFocus() {
            const target = document.activeElement === lockCancelButton.value ? lockSaveButton.value : lockCancelButton.value
            target?.focus()
        }
        function canLeave() {
            if (showLockConfirm.value) return false
            if (saving.value) return false
            if (!dirty.value) return true
            if (discardPromise) return discardPromise
            previousFocus = document.activeElement
            showDiscard.value = true
            discardPromise = new Promise(resolve => { discardResolver = resolve })
            nextTick(() => cancelButton.value?.focus())
            return discardPromise
        }
        function resolveDiscard(leave) {
            showDiscard.value = false
            discardResolver?.(leave)
            discardResolver = null; discardPromise = null
            if (!leave) nextTick(() => previousFocus?.focus())
        }
        function cycleDialogFocus() {
            const target = document.activeElement === cancelButton.value ? discardButton.value : cancelButton.value
            target?.focus()
        }
        onBeforeRouteLeave(canLeave)
        onBeforeRouteUpdate(canLeave)
        function beforeUnload(event) {
            if (dirty.value || saving.value) { event.preventDefault(); event.returnValue = '' }
        }
        onMounted(() => {
            window.addEventListener('beforeunload', beforeUnload)
            window.addEventListener('visibilitychange', foreground)
            clockTimer = setInterval(tickClock, 1000)
        })
        onBeforeUnmount(() => { generation++; resolveDiscard(false); resolveLockConfirm(false); clearInterval(clockTimer); window.removeEventListener('visibilitychange', foreground); window.removeEventListener('beforeunload', beforeUnload) })
        return { PHOTO_ACCEPT, isLocked, lockLabel, clockNow, refreshLocks, showLockConfirm, lockCancelButton, lockSaveButton, resolveLockConfirm, cycleLockFocus, home, draft, loading, saving, choosingPhoto, loadError, saveError, retryKey, isPhotos, limit, dirty, showDiscard, cancelButton, discardButton, resolveDiscard, cycleDialogFocus, remove, choosePhoto, save }
    }
}
</script>

<style scoped>
.home-editor { --green: var(--home-green); --pink: #cc1255; min-height: 100dvh; display: flex; flex-direction: column; }
.home-editor button { border: 0; background: transparent; font: inherit; cursor: pointer; }
.home-editor button:disabled { cursor: default; opacity: .5; }
.home-editor__description { font-family: 'Montserrat', sans-serif; font-weight: 500; font-size: 10px; line-height: 12px; color: var(--home-muted); margin-bottom: 17px; }
.home-editor__description--vehicles { min-height: 24px; margin-bottom: 12px; }
.home-editor__selected-photo { overflow-wrap: anywhere; margin: 8px 0 0; }
.home-editor__form { flex: 1; display: flex; flex-direction: column; }
.home-editor__fields { border: 0; padding: 0; margin: 0; min-width: 0; }
.home-editor__grid { display: grid; grid-template-columns: repeat(3, 90px); justify-content: space-between; gap: 24px 12px; padding: 0 6px 0 3px; }
.home-editor__grid--vehicles { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px 12px; padding: 0 16px 0 13px; }
.home-editor__item { min-width: 0; }
.home-editor__corners { position: relative; display: block; }
.home-editor__corners::before, .home-editor__corners::after { content: ''; position: absolute; inset: 0; pointer-events: none; background: linear-gradient(currentColor,currentColor) left top/8px 1px no-repeat, linear-gradient(currentColor,currentColor) right top/8px 1px no-repeat, linear-gradient(currentColor,currentColor) left bottom/8px 1px no-repeat, linear-gradient(currentColor,currentColor) right bottom/8px 1px no-repeat; }
.home-editor__corners::after { background: linear-gradient(currentColor,currentColor) left top/1px 8px no-repeat, linear-gradient(currentColor,currentColor) right top/1px 8px no-repeat, linear-gradient(currentColor,currentColor) left bottom/1px 8px no-repeat, linear-gradient(currentColor,currentColor) right bottom/1px 8px no-repeat; }
.home-editor__portrait { width: 100%; aspect-ratio: 1; padding: 3px; color: #cacaca; background: transparent; cursor: pointer; }
.home-editor__portrait img, .home-editor__portrait svg { display: block; width: 100%; height: 100%; object-fit: cover; background: rgb(202 202 202 / .1); }
.home-editor__portrait svg { fill: none; stroke: #cacaca; stroke-width: 2; }
.home-editor__portrait .home-editor__plus { stroke-width: 4; }
.home-editor__portrait input { position: absolute; inset: 0; opacity: 0; width: 100%; cursor: pointer; }
.home-editor__portrait:focus-within, .home-editor__name:focus-within { outline: 1px solid var(--green); outline-offset: 3px; }
.home-editor__name { color: var(--green); margin-top: 20px; padding: 3px; background: #22251c; }
.home-editor__name input { background: transparent; border: 0; outline: 0; width: 100%; min-width: 0; color: var(--green); text-align: center; font-family: 'Montserrat', sans-serif; font-weight: 500; font-size: 11px; line-height: 13px; height: 22px; }
.home-editor__name input::placeholder { color: var(--green); opacity: 1; }
.home-editor .home-editor__delete { display: block; color: var(--pink); background: #251c24; width: 75%; height: 38px; margin: 20px auto 0; }
.home-editor__delete svg { width: 24px; height: 24px; fill: none; stroke: currentColor; stroke-width: 1.5; }
.home-editor__plate { position: relative; container-type: inline-size; }
.home-editor__plate :deep(.home-license-plate) { aspect-ratio: 157 / 36; }
.home-editor__plate input { position: absolute; left: 12%; top: 14%; width: 84%; height: 70%; background: transparent; color: #040404; text-align: center; border: 0; font: 600 15px 'u_DIN 1451 Mittelschrift', sans-serif; text-transform: uppercase; }
.home-editor__plate input:disabled { opacity: 1; cursor: default; -webkit-text-fill-color: #040404; }
.home-editor__lock { margin: 10px auto 0; min-height: 38px; display: flex; align-items: center; justify-content: center; text-align: center; color: var(--home-muted); font: 11px/16px 'Montserrat', sans-serif; }
.home-editor__plate--empty input { background: #3b3b3b; color: var(--green); }
.home-editor__plate input::placeholder { color: var(--green); }
.home-editor .home-editor__save { position: sticky; bottom: 20px; width: 100%; margin-top: auto; padding: 0; color: #a4c746; background: transparent; border: 0; }
.home-editor__save svg { display: block; width: 100%; height: auto; }
.home-editor__save-label { position: absolute; inset: 0; display: flex; justify-content: center; align-items: center; color: #000; font-size: 14px; }
.home-editor__save:focus-visible { outline: 1px solid var(--green); outline-offset: 4px; }
@media (max-width: 340px) { .home-editor__grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } .home-editor__grid--vehicles { grid-template-columns: repeat(2, minmax(0, 1fr)); padding: 0; } }
@media (max-width: 305px) { .home-editor__save-label { font-size: 10px; } }
.home-editor__fields { margin-bottom: 80px; }
.home-editor__status { margin: 20px 0; font: 13px/20px 'Montserrat', sans-serif; text-align: center; }
.home-editor__status button { color: var(--green); margin-top: 18px; }
.home-editor__error { color: #e18ba8; }
.home-editor__sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
.home-editor__overlay { position: fixed; inset: 0; z-index: 1000; background: #000a; display: grid; place-items: center; padding: 20px; }
.home-editor__dialog { width: 100%; max-width: 360px; background: #1b1c22; border: 1px solid #a4c746; padding: 24px; color: #cacad0; }
.home-editor__dialog h2 { color: #a4c746; font-size: 16px; font-weight: normal; margin-bottom: 16px; }
.home-editor__dialog p { font: 13px/20px 'Montserrat', sans-serif; margin-bottom: 20px; }
.home-editor__dialog button { display: block; width: 100%; min-height: 44px; border: 1px solid #a4c746; background: #414e22; color: #a4c746; margin-top: 12px; cursor: pointer; }
.home-editor__dialog .home-editor__discard { color: #e18ba8; border-color: #a7375c; background: #251c24; }
@media (max-width: 360px) { .home-editor__grid { row-gap: 20px; } }
</style>
